import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Target, FileText, Bookmark, Award, Phone, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const buildActions = (user) => {
  if (!user) return [];

  const savedCollegesCount = Array.isArray(user.saved_colleges) ? user.saved_colleges.length : 0;
  const savedScholarshipsCount = Array.isArray(user.saved_scholarships)
    ? user.saved_scholarships.length
    : 0;

  const candidates = [
    {
      condition: !user.intended_major,
      icon: BookOpen,
      title: 'Add your intended major',
      description: 'Improves your college and scholarship matches',
      ctaLabel: 'Add Major',
      ctaHref: '/account/settings',
    },
    {
      condition: !user.gpa,
      icon: Target,
      title: 'Add your GPA',
      description: 'Required for accurate college match scoring',
      ctaLabel: 'Add GPA',
      ctaHref: '/account/settings',
    },
    {
      condition: !user.sat_score && !user.act_score,
      icon: FileText,
      title: 'Add test scores',
      description: 'SAT or ACT scores help refine your matches',
      ctaLabel: 'Add Scores',
      ctaHref: '/account/settings',
    },
    {
      condition: savedCollegesCount === 0,
      icon: Bookmark,
      title: 'Save your first college',
      description: 'Start building your college list',
      ctaLabel: 'Explore Colleges',
      ctaHref: '/explore/colleges',
    },
    {
      condition: savedScholarshipsCount === 0,
      icon: Award,
      title: 'Save your first scholarship',
      description: 'Find funding opportunities that fit you',
      ctaLabel: 'Find Scholarships',
      ctaHref: '/explore/scholarships',
    },
    {
      condition: !user.phone,
      icon: Phone,
      title: 'Complete your contact info',
      description: 'Add your phone number to your profile',
      ctaLabel: 'Update Profile',
      ctaHref: '/account/settings',
    },
  ];

  return candidates.filter((a) => a.condition).slice(0, 4);
};

const NextBestActions = () => {
  const { user } = useAuth();
  const actions = buildActions(user);

  if (!user) return null;

  if (actions.length === 0) {
    return (
      <div
        className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-center gap-4"
        data-testid="next-best-actions-complete"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#F0FDF4' }}
        >
          <CheckCircle size={22} style={{ color: '#1a5d3a' }} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            Your profile is looking great
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Keep building your list.{' '}
            <Link
              to="/explore/colleges"
              className="font-medium underline underline-offset-2"
              style={{ color: '#1a5d3a' }}
              data-testid="next-actions-explore-link"
            >
              Explore more colleges
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="next-best-actions">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Next Best Actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <div
              key={action.title}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              data-testid={`action-card-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={18} style={{ color: '#1a5d3a' }} aria-hidden="true" />
                <div className="text-sm font-semibold text-gray-900">{action.title}</div>
              </div>
              <div className="text-xs text-gray-500 mb-3">{action.description}</div>
              <Link
                to={action.ctaHref}
                className="inline-block text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                style={{ backgroundColor: '#F0FDF4', color: '#1a5d3a' }}
                data-testid={`action-cta-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {action.ctaLabel}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NextBestActions;
